<script lang="ts">
	import { format } from 'src/services/i18n';
	import { locale } from 'svelte-i18n';
	import DocumentContextualNavigation from 'src/components/DocumentContextualNavigation.svelte';
	import DocumentContextualNavigationV2 from 'src/components/DocumentContextualNavigationV2.svelte';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import { get } from 'svelte/store';
	import { CHANGE_DATE } from 'src/constants';
	import { localizedLastUpdate } from 'src/helpers/localized_last_update_helper';

	$: menuItems = [
		{
			id: 'introduction',
			label: $format('text_privacy_policy_header_introduction', {
				default: '1. INTRODUCTION',
			}),
			active: true,
		},
		{
			id: 'controller',
			label: $format('text_privacy_policy_header_controller', {
				default: '2. CONTROLLER',
			}),
		},
		{
			id: 'personal-data',
			label: $format('text_privacy_policy_header_personal_data', {
				default: '3. PERSONAL DATA',
			}),
		},
		{
			id: 'data-subject',
			label: $format('text_privacy_policy_header_data_subject', {
				default: '4. DATA SUBJECT',
			}),
		},
		{
			id: 'data-protection-officer',
			label: $format('text_privacy_policy_header_data_protection', {
				default: '5. DATA PROTECTION OFFICER',
			}),
		},
		{
			id: 'shelly-service',
			label: $format('text_privacy_policy_header_shelly_service', {
				default: '6. ABOUT THE SHELLY SERVICE',
			}),
		},
		{
			id: 'types-personal-data',
			label: $format('text_privacy_policy_header_types_personal_data', {
				default: '7. TYPES OF PERSONAL DATA THAT WE PROCESS FOR THE PROVISION OF THE SERVICE',
			}),
		},
		{
			id: 'data-transfers',
			label: $format('text_privacy_policy_header_cross_border_data', {
				default: '8. CROSS-BORDER DATA TRANSFERS',
			}),
		},
		{
			id: 'retention-of-data',
			label: $format('text_privacy_policy_header_retention_data', {
				default: '9. RETENTION OF DATA',
			}),
		},
		{
			id: 'rights',
			label: $format('text_privacy_policy_header_rights', {
				default: '10. YOUR RIGHTS AS DATA SUBJECT',
			}),
		},
		{
			id: 'security-measures',
			label: $format('text_privacy_policy_header_security_measures', {
				default: '11. INFORMATION SECURITY MEASURES',
			}),
		},
		{
			id: 'updates',
			label: $format('text_privacy_policy_header_updates', {
				default: '12. PRIVACY POLICY UPDATES',
			}),
		},
		{
			id: 'further-information',
			label: $format('text_privacy_policy_header_further_info', {
				default: '13. FURTHER INFORMATION',
			}),
		},
	];

	const linkClass = get(showNewLayout) ? 'link-styles hover-opacity' : 'link-styles';
	const urlDelete = `<a class="${linkClass}" target="_blank" href="https://control.shelly.cloud/#/request-account-delete">https://control.shelly.cloud/#/request-account-delete</a>`;
	const urlShellyControl = `<a class="${linkClass}" target="_blank" href="https://control.shelly.cloud/">https://control.shelly.cloud/</a>`;

	$: sectionsContent = [
		{
			id: 'introduction',
			title: $format('text_privacy_policy_header_introduction', {
				default: '1. INTRODUCTION',
			}),
			bolded: $format('text_privacy_policy_intro_bolded', {
				default:
					'“Shelly Cloud Service” branded “Shelly Smart Control” is digital service that allows remote access, control and monitoring of Shelly Devices and the appliances to which these are attached. All applications developed by Shelly Europe Ltd. that enable access to, control of or monitoring of Shelly Devices and process personal data originating from “Shelly Cloud Service” shall be deemed integral to „Shelly Cloud Service“, notwithstanding any differing naming. Functional variations between these other applications and Shelly Smart Control may occur.',
			}),
			titleDesc: [
				$format('text_privacy_policy_intro_titleDesc_1', {
					default: `This Privacy Policy shall apply to the processing of personal data, that is or might be performed when using Shelly Cloud Service accessed by using either Android, Huawei or iOS mobile application Shelly Smart Control or any internet browser at ${urlShellyControl}, whereas the Shelly Cloud Service might be used as free of charge (Standard Service) or as pre-paid subscription service (Premium Service) referred to in this document in general as “the Shelly Service” / “the Service”.`,
					values: { url: urlShellyControl },
				}),
				$format('text_privacy_policy_intro_titleDesc_2', {
					default:
						'When you interact with the Shelly Service, Shelly Europe Ltd. (“We”) are processing your Personal Data. We have developed this Privacy Notice to provide you with information about what information we collect, why we collect it, and what are your rights under the applicable data protection laws, including the European Union’s General Data Protection Regulation (“GDPR”).',
				}),
			],
		},
		{
			id: 'controller',
			title: $format('text_privacy_policy_header_controller', {
				default: '2. CONTROLLER',
			}),
			titleDesc: [
				$format('text_privacy_policy_controller_titleDesc_1', {
					default:
						'The Personal Data related to Shelly Cloud Service are processed by Shelly Europe Ltd., UIC: 202320104, having its seat and registered address in Europe, Bulgaria, 1407 Sofia, No 51 Cherni Vruh Blvd., Building 3, floor 2 and 3.',
				}),
			],
		},
		{
			id: 'personal-data',
			title: $format('text_privacy_policy_header_personal_data', {
				default: '3. PERSONAL DATA',
			}),
			titleDesc: [
				$format('text_privacy_policy_personal_data_titleDesc_1', {
					default:
						'Personal data means any information relating to an identified or identifiable natural person (‘data subject’); an identifiable natural person is one who can be identified, directly or indirectly, in particular by reference to an identifier such as a name, an identification number, location data, an online identifier or to one or more factors specific to the physical, physiological, genetic, mental, economic, cultural or social identity of that natural person, as that term is defined under the General Data Protection Regulation (GDPR).',
				}),
			],
		},
		{
			id: 'data-subject',
			title: $format('text_privacy_policy_header_data_subject', {
				default: '4. DATA SUBJECT',
			}),
			titleDesc: [
				$format('text_privacy_policy_data_subject_titleDesc_1', {
					default:
						'The Data Subjects to whom this Privacy Notice shall apply is any registered user of the Shelly Service (“the User” / “You”).',
				}),
			],
		},
		{
			id: 'data-protection-officer',
			title: $format('text_privacy_policy_header_data_protection', {
				default: '5. DATA PROTECTION OFFICER',
			}),
			titleDesc: [
				$format('text_privacy_policy_data_protection_titleDesc_1', {
					default:
						'You may address all your requests about the processing of your Personal Data to our Data Protection Officer via e-mail at dpo@shelly.com',
				}),
			],
		},
		{
			id: 'shelly-service',
			title: $format('text_privacy_policy_header_shelly_service', {
				default: '6. ABOUT THE SHELLY SERVICE',
			}),
			titleDesc: [
				$format('text_privacy_policy_shelly_service_titleDesc_1', {
					default:
						'Shelly Service allows you to monitor and control home and office appliances, get precise measurements of the power consumption of each appliance, and switch it on/off remotely. The Service might be accessed using Your mobile device and/or a computer, laptop or tablet (“Terminal Device(s)”)',
				}),
				$format('text_privacy_policy_shelly_service_titleDesc_2', {
					default:
						'The Service is related to the usage of Shelly Devices which is any smart device branded „Shelly” or owned by the Controller (Shelly Device(s)) in all models and modifications, that upon adding to a User’s account allows remote monitoring and control of home and office appliances which this device is attached to.',
				}),
				$format('text_privacy_policy_shelly_service_titleDesc_3', {
					default:
						'The information that we collect, and how that information is used, depends on how you use our Service and our Shеlly Device(s) and how you manage your privacy controls via the Service settings and the settings of the Terminal Device on which you use it.',
				}),
				$format('text_privacy_policy_shelly_service_titleDesc_4', {
					default:
						'For security reasons, each Shelly Device is associated with only one Account and is assigned to a specific House within that Account. An Account may contain one or more Houses, each of which represents a separate physical site, dwelling or premises (e.g. home, holiday house, office, where the device physically exists). Shelly Devices, automations, scenes, schedules and House-level subscriptions are configured at the level of a specific House.',
				}),
				$format('text_privacy_policy_shelly_service_titleDesc_5', {
					default:
						'The User who creates a House (or to whom a House has been transferred) is the Main User of that House. The Main User has full management rights over the House and may, through the Shelly Service, (i) share access to a House with one or more other Users (Shared Users), determining and at any time modifying or revoking the scope of access granted to each Shared User; and (ii) irrevocably transfer the House (together with all assigned Shelly Devices and, where transferable, the active House-level subscriptions linked to it) to another User’s Account (House Transfer). Sharing or transferring a House results in the sharing or transfer of the data associated with the relevant House and its Shelly Devices to the recipient User, within the scope determined by the action initiated by the Main User. Further information on the rights of the Main User, the Shared User, sharing and House Transfer is set out in the General Terms and Conditions of the Shelly Cloud Service.',
				}),
				$format('text_privacy_policy_shelly_service_titleDesc_6', {
					default:
						'Where You act as Main User of a House and decide to share access to that House with a Shared User or to transfer the House to another Account, You are responsible for ensuring that such sharing or transfer is lawful in light of applicable data protection and privacy law, including (where required) for obtaining and documenting any consents or informing other affected persons. Shelly Europe Ltd. does not act as a mediator between Users in respect of House sharing or House Transfer.',
				}),
			],
		},
		{
			id: 'types-personal-data',
			title: $format('text_privacy_policy_header_types_personal_data', {
				default: '7. TYPES OF PERSONAL DATA THAT WE PROCESS FOR THE PROVISION OF THE SERVICE',
			}),
			bolded: $format('text_privacy_policy_personal_data_bolded', {
				default:
					'On a contractual basis for the provision of the Service in accordance with Art. 6(1)(b) of GDPR We process data about:',
			}),
			titleDesc: [
				$format('text_privacy_policy_personal_data_titleDesc', {
					default:
						'Your Account, your Shelly Device(s) and the Terminal Devices on which the Service is used, as well as data derived from the settings of the Shelly Service and/or the settings of your Associate Services or browsers, Your interaction with the Service.',
				}),
			],
			subtitles: [
				{
					title: $format('text_privacy_policy_personal_data_subtitle_1', {
						default: 'Account data',
					}),
					description: [
						$format('text_privacy_policy_personal_data_subtitle_desc_1.1', {
							default:
								'We process Account data that you have directly provided to us when creating Your Account, interacting with the Service using its features or when contacting our support team directly:',
						}),
						$format('text_privacy_policy_personal_data_subtitle_desc_1.2', {
							default: '⦁	Email address;',
						}),
						$format('text_privacy_policy_personal_data_subtitle_desc_1.3', {
							default: '⦁	Password;',
						}),
						$format('text_privacy_policy_personal_data_subtitle_desc_1.4', {
							default: '⦁	Account ID;',
						}),
						$format('text_privacy_policy_personal_data_subtitle_desc_1.5', {
							default: '⦁	Time zone settings;',
						}),
						$format('text_privacy_policy_personal_data_subtitle_desc_1.6', {
							default: '⦁	Language settings;',
						}),
						$format('text_privacy_policy_personal_data_subtitle_desc_1.7', {
							default: '⦁	Data generated by you during a customer support request;',
						}),
						$format('text_privacy_policy_personal_data_subtitle_desc_1.8', {
							default:
								'⦁	Data related to customer support such as e-mails, messages, history of the correspondence, any correspondence content, sender and recipient information;',
						}),
						$format('text_privacy_policy_personal_data_subtitle_desc_1.9', {
							default:
								'⦁	Payment details that are necessary for in-app purchases and invoicing;',
						}),
						$format('text_privacy_policy_personal_data_subtitle_desc_2', {
							default:
								'⦁	Voucher activation code and redemption timestamp (processed only when the voucher is redeemed in your Account);',
						}),
					],
				},
				{
					title: $format('text_privacy_policy_personal_data_subtitle_camera', {
						default: 'Shelly Camera Data (where You use a Shelly Camera)',
					}),
					description: [
						$format('text_privacy_policy_personal_data_subtitle_desc_camera_1', {
							default:
								'Where You add a Shelly Camera to a House within Your Account, We additionally process the following categories of Personal Data generated by Your interaction with the Shelly Camera and the related cloud features (including, where applicable, the Cloud Camera Storage Service):',
						}),
						$format('text_privacy_policy_personal_data_subtitle_desc_camera_2', {
							default:
								'⦁	video recordings, snapshots and live-stream content captured by the Shelly Camera;',
						}),
						$format('text_privacy_policy_personal_data_subtitle_desc_camera_3', {
							default:
								'⦁	audio recordings and two-way audio streams transmitted through the Shelly Camera;',
						}),
						$format('text_privacy_policy_personal_data_subtitle_desc_camera_4', {
							default:
								'⦁	motion-detection event logs, event timestamps and associated technical metadata;',
						}),
						$format('text_privacy_policy_personal_data_subtitle_desc_camera_5', {
							default:
								'N.B. AI Detection output is limited to general categories (e.g. “person”, “vehicle”, “animal”), accompanied by the certainty percentage produced by the AI Detection feature each time it analyses the video stream of the Shelly Camera. The feature only classifies what is visible in the video frame at a general category level; it does not identify any specific natural person and does not constitute biometric identification or biometric categorisation.',
						}),
						$format('text_privacy_policy_personal_data_subtitle_desc_camera_6', {
							default:
								'⦁	Shelly Camera configuration data, including the activation status of Privacy Mode and the Cloud Camera Storage Service plan (where applicable) assigned to each Shelly Camera within Your Standard Service/Premium Service (collectively, together with the above bullets, the “Camera Content”).',
						}),
					],
				},
				{
					title: $format('text_privacy_policy_personal_data_subtitle_camera_purposes', {
						default: 'Purposes for processing Camera Content:',
					}),
					description: [
						$format('text_privacy_policy_personal_data_subtitle_desc_camera_purposes_1', {
							default:
								'We process the Camera Content on a contractual basis in accordance with Art. 6(1)(b) GDPR in order to: (i) enable live video and audio streaming, recording and two-way communication features; (ii) operate motion-detection and event-notification functionalities; (iii) provide automated classification of visible objects, persons, animals or events through the AI Detection feature; (iv) make available, under the Cloud Camera Storage Service, extended cloud-recording storage and retrieval; and (v) ensure the security and proper functioning of the Shelly Camera and the related cloud infrastructure.',
						}),
						$format('text_privacy_policy_personal_data_subtitle_desc_camera_purposes_2', {
							default:
								'In accordance with Article 50 of Regulation (EU) 2024/1689 (AI Act), We inform You that AI Detection is an artificial intelligence feature. AI Detection results are intended as informational signals only; they must not be relied upon as an authoritative determination of fact, in particular for safety, security or evidentiary purposes, and they are not used by Shelly Europe Ltd. to take automated decisions producing legal or similarly significant effects on data subjects within the meaning of Article 22 GDPR. AI Detection processes the Camera Content for the limited purpose of generating classifications and labels and is not used to train artificial intelligence models on Your Camera Content unless You have provided Your separate explicit consent for such use.',
						}),
						$format('text_privacy_policy_personal_data_subtitle_desc_camera_purposes_3', {
							default:
								'Where You install a Shelly Camera in a location in which other persons may be recorded, You acknowledge that You may act as a data controller in Your own right in respect of those persons and that You are responsible for complying with the obligations imposed on controllers by applicable data protection and privacy law, including providing appropriate notice and, where required, obtaining consent.',
						}),
					],
				},
				{
					title: $format('text_privacy_policy_personal_data_subtitle_2', {
						default: 'Purpose for processing Account Data:',
					}),
					description: [
						$format('text_privacy_policy_personal_data_subtitle_desc_2.1', {
							default:
								'We are using the Account data for the purpose of performing the Service contract in accordance with Art. 6(1)(b) GDPR to:',
						}),
						$format('text_privacy_policy_personal_data_subtitle_desc_2.2', {
							default:
								'⦁	verify the User’s authentication and identification in order to ensure access control to Your Account and Your Shelly Device(s);',
						}),
						$format('text_privacy_policy_personal_data_subtitle_desc_2.3', {
							default:
								'⦁	Initiate and provide the Service to You and to ensure its proper functioning;',
						}),
						$format('text_privacy_policy_personal_data_subtitle_desc_2.4', {
							default: '⦁	Ensure the security of your Account and your Shelly Device(s);',
						}),
						$format('text_privacy_policy_personal_data_subtitle_desc_2.5', {
							default:
								'⦁	Process of customer support rеquests that You might have send to us in reference to the Service and the Shelly Devices;',
						}),
						$format('text_privacy_policy_personal_data_subtitle_desc_2.6', {
							default:
								'⦁	Facilitate payments and invoicing as well as subscriptions and their termination or renewal;',
						}),
						$format('text_privacy_policy_personal_data_subtitle_desc_2.7', {
							default:
								'⦁	Communicate with You in reference to the Service and send you Service updates or other relevant Service information (including reminders, confirmations and invoices regarding Premium subscriptions), as well as providing you the necessary customer support when requested;',
						}),
					],
				},
				{
					title: $format('text_privacy_policy_personal_data_subtitle_3', {
						default: 'Device Data and Service Usage',
					}),
					description: [
						$format('text_privacy_policy_personal_data_subtitle_desc_3.1', {
							default:
								'For providing You with the Service We are also processing information about the Shelly Devices that are added to Your Account including the information about Your interaction with the Service, as well as information about any Terminal Devices on which you have installed and accessed the Service such as computer, laptop, tablet, mobile device, that is technically necessary for the provision of the Service',
						}),
						$format('text_privacy_policy_personal_data_subtitle_desc_3.2', {
							default:
								'By adding the Shelly Device to Your Account, you are sharing the information generated and stored by Your Device (such as set up actions, schedules, modes, timers or other configurations that you have set up on the Device). This information is necessary for providing you the Service and the functioning of its features. For stop sharing this information You can always remove the Shelly Device from the Service.',
						}),
						$format('text_privacy_policy_personal_data_subtitle_desc_3.3', {
							default:
								'When You install, access or use our Service, including when You are adding a Shelly Device to Your User Account, as well as when you share information from the Shelly Service with other applications and services (Associated Services / Integrators) We automatically process:',
						}),
						$format('text_privacy_policy_personal_data_subtitle_desc_3.4', {
							default:
								'⦁	Shelly Device identifiers such as access point (SSID), Device IP, Device type, decimal ID, Device name and channel, Version of Device Firmware, Bluethoot data (if available), mac address of the Device, other configuration data;',
						}),
						$format('text_privacy_policy_personal_data_subtitle_desc_3.5', {
							default: '⦁	Network and Server credentials;',
						}),
						$format('text_privacy_policy_personal_data_subtitle_desc_3.6', {
							default:
								'⦁	Information about associated Services, browsers and Terminal Devices that You use to access our Service including Terminal Device type and network provider, unique identifiers such as IP address and MAC address, operating system and OS version;',
						}),
						$format('text_privacy_policy_personal_data_subtitle_desc_3.7', {
							default:
								'⦁	Log information such as Service logs, including crash and diagnostic reports, Service launches, taps, clicks, or other information about how the user interacts with the Service;',
						}),
						$format('text_privacy_policy_personal_data_subtitle_desc_3.8', {
							default: '⦁	 Geolocation of the Device;',
						}),
						$format('text_privacy_policy_personal_data_subtitle_desc_3.9', {
							default:
								'⦁	Event logs and event log settings, such as on/of modes, schedules, actions, device measurements and electricity consumption data depending on the Shelly Device type and the available Service options (including historical consumption and measurements data);',
						}),
						$format('text_privacy_policy_personal_data_subtitle_desc_3.10', {
							default:
								'⦁	Other information about how you’re using our Service depending on the Device type and features;',
						}),
					],
				},
				{
					title: $format('text_privacy_policy_personal_data_subtitle_4', {
						default: 'Purpose for processing Data of Your Devices',
					}),
					description: [
						$format('text_privacy_policy_personal_data_subtitle_desc_4.1', {
							default:
								'⦁	Provision of the Service – to enable You to monitor and control your home and/or office appliances, get precise measurements of the power consumption of appliances (including historical data), switch them on/off remotely and performing the set up actions, we need information about the integrated Shelly Devices, the Terminal Device on which you are using the Service and their connectivity to each other, to the network and to our system. Further to provide you with some features of the Service such as the scheduling and powermetering we need to know the geolocation of the Device;',
						}),
						$format('text_privacy_policy_personal_data_subtitle_desc_4.2', {
							default: '⦁	Processing of customer support rеquests;',
						}),
						$format('text_privacy_policy_personal_data_subtitle_desc_4.3', {
							default:
								'⦁	Sending of In-App, including push-up, or e-mail system notifications about the Service, including event notifications if set-up;',
						}),
					],
				},
				{
					title: $format('text_privacy_policy_personal_data_subtitle_5', {
						default:
							'On the basis of your explicit consent, expressed via the Terminal Device settings or by deliberate actions within the Service we may process further Device Data and Service Usage in accordance with Art. 6(1)(a) of GDPR.',
					}),
					description: [
						$format('text_privacy_policy_personal_data_subtitle_desc_5.1', {
							default:
								'For providing You certain specific features of the Service, depending on the type of the Shelly Device and the available Service options We may also process further information about the Shelly Devices that you have manually set using the Service settings.',
						}),
					],
				},
				{
					title: $format('text_privacy_policy_personal_data_subtitle_6', {
						default:
							'Data derived from the settings of the Shelly Service and/or the settings of your Associate Services, Terminal Devices or browsers;',
					}),
					description: [
						$format('text_privacy_policy_personal_data_subtitle_desc_6.1', {
							default:
								'⦁	Content that you create and upload in the Service (images, videos);',
						}),
						$format('text_privacy_policy_personal_data_subtitle_desc_6.2', {
							default: '⦁	 Security access keys such as lock pin, access key;',
						}),
						$format('text_privacy_policy_personal_data_subtitle_desc_6.3', {
							default: '⦁	 Device sharing;',
						}),
						$format('text_privacy_policy_personal_data_subtitle_desc_6.4', {
							default:
								'⦁	Interactions history of the Service and Shelly Device(s) usage (user logs);',
						}),
						$format('text_privacy_policy_personal_data_subtitle_desc_6.5', {
							default:
								'⦁	Other information of your choice, that you provide through the settings of the Service, Associated Service or Terminal Devices;',
						}),
						$format('text_privacy_policy_personal_data_subtitle_desc_6.6', {
							default: '⦁	Other information about the way you use the Shelly Device(s);',
						}),
						$format('text_privacy_policy_personal_data_subtitle_desc_6.7', {
							default:
								'You may control the processing of these data at any time through the settings of the Service and the Terminal Device on which you use it. These shall not prejudice the legality of the processing based on your interactions with the Service and settings given before their change.',
						}),
						$format('text_privacy_policy_personal_data_subtitle_desc_6.8', {
							default:
								'You can initiate and stop sharing information of a selected Shelly Device with third parties and services at any time by using the features of the Service. Please take into account that upon sharing Your Data with third parties and services this Privacy Notice shall not apply to the processing of the shared Data by these parties and services.',
						}),
					],
				},
				{
					title: $format('text_privacy_policy_personal_data_subtitle_7', {
						default: 'Purpose for processing Data of your Devices',
					}),
					description: [
						$format('text_privacy_policy_personal_data_subtitle_desc_7.1', {
							default:
								'⦁	Provide you personalized information and solutions based on your interactions with the App and the Shelly Devices such as monitor and controlling your home and/or office appliances, precise measurements and consumption, and switching on/off remotely according to the schedule or the set technical criteria;',
						}),
						$format('text_privacy_policy_personal_data_subtitle_desc_7.2', {
							default:
								'⦁	Authentication, verification and control of the access to your Shelly Device(s) including enabling and control of device sharing;',
						}),
						$format('text_privacy_policy_personal_data_subtitle_desc_7.3', {
							default:
								'⦁	Sending of notifications (including In-App notifications such as push-up or e-mail) based on event log of the Shelly Device;',
						}),
						$format('text_privacy_policy_personal_data_subtitle_desc_7.4', {
							default: '⦁	 Processing of support rеquests;',
						}),
					],
				},
				{
					title: $format('text_privacy_policy_personal_data_subtitle_8', {
						default:
							'⦁	Further Account and Device Data that we process based on your explicit consent:',
					}),
					description: [
						$format('text_privacy_policy_personal_data_subtitle_desc_8.1', {
							default:
								'⦁	E-mail Address and Account Data for sending You In-App, push-up and e-mail marketing notifications about Shelly products and Services, including personalized ones if you have opted-in accordingly;',
						}),
						$format('text_privacy_policy_personal_data_subtitle_desc_8.2', {
							default:
								'You may withdraw your consent at any time through the unsubscribe link at the bottom of the relevant marketing message, as well as through the settings of Your Service Account. Upon withdrawal of the consent, processing of the respective type of personal data shall be terminated for the stated purposes. Withdrawal of consent shall not prejudice the legality of the processing based on consent given before its withdrawal.',
						}),
					],
				},
				{
					title: $format('text_privacy_policy_personal_data_subtitle_9', {
						default:
							'Based on our legitimate interest in accordance with Art. 6(1)(f) of GDPR we may process Your personal data, as described here above to:',
					}),
					description: [
						$format('text_privacy_policy_personal_data_subtitle_desc_9.1', {
							default: '⦁	 implement and operate our policies and procedures;',
						}),
						$format('text_privacy_policy_personal_data_subtitle_desc_9.2', {
							default:
								'⦁	respond to any claims against us and to protect the rights, privacy, property, or safety of Shelly Europe Ltd. (including its affiliates and subsidiaries), our Users, or the public as required or permitted by law;',
						}),
						$format('text_privacy_policy_personal_data_subtitle_desc_9.3', {
							default:
								'⦁	enforce legal claims, including investigation of potential violations of the applicable terms of Service;',
						}),
						$format('text_privacy_policy_personal_data_subtitle_desc_9.4', {
							default:
								'⦁	detect, prevent, or otherwise address fraud, abuse, security, or technical issues with the Service;',
						}),
						$format('text_privacy_policy_personal_data_subtitle_desc_9.5', {
							default:
								'⦁	prevent hacking, frauds and other non-compliant use of the Service;',
						}),
						$format('text_privacy_policy_personal_data_subtitle_desc_9.6', {
							default:
								'⦁	register, mediate, and resolve possible disputes or irregularities or to enforce our Terms of Use and other policies;',
						}),
						$format('text_privacy_policy_personal_data_subtitle_desc_9.7', {
							default:
								'⦁	maintain adequate security measures and protect against liability, including complying with industry standards and enforcing our policies, detection of spam, malware, illegal content, and other forms of abuse on our systems in violation of our security policies;',
						}),
						$format('text_privacy_policy_personal_data_subtitle_desc_9.8', {
							default: '⦁	 fulfill the terms and conditions of the Service;',
						}),
						$format('text_privacy_policy_personal_data_subtitle_desc_9.9', {
							default: '⦁	other legitimate business purposes permitted by applicable law',
						}),
						$format('text_privacy_policy_personal_data_subtitle_desc_9.10', {
							default:
								'⦁	anonymized and statistic data about the usage of our Service and the users experience for the purposes of operating, evaluating, and improving the Service and our business;',
						}),
						$format('text_privacy_policy_personal_data_subtitle_desc_9.11', {
							default:
								'⦁	maintain the Service to ensure it is working as intended by performing regular monitoring of our system and activity information to identify and fix problems and avoid interruption;',
						}),
						$format('text_privacy_policy_personal_data_subtitle_desc_9.12', {
							default:
								'⦁	improve the Service through analysis of the usage trends and preferences;',
						}),
						$format('text_privacy_policy_personal_data_subtitle_desc_9.13', {
							default:
								'⦁	send marketing information through notifications (such as pop-ups, e-mail, push notification) related to special offers, discounts, promotions, new features, and other marketing content about Shelly devices and Services, including personalized ones. You can easily opt-out at any time, through the setting manual of the Terminal Device/the Service or via an active “unsubscribe” link provided with each marketing message, as might be relevant depending on the relevant communication channel type;',
						}),
					],
				},
				{
					title: $format('text_privacy_policy_personal_data_subtitle_10', {
						default:
							'⦁	Based on the law when it is necessary or appropriate, we can process Your Personal data in accordance with Art. 6(1)(с) of GDPR to:',
					}),
					description: [
						$format('text_privacy_policy_personal_data_subtitle_10.1', {
							default:
								'⦁	comply with applicable laws and regulations, including in the field of tax and accounting;',
						}),
						$format('text_privacy_policy_personal_data_subtitle_10.2', {
							default: '⦁  comply with legal procedures;',
						}),
						$format('text_privacy_policy_personal_data_subtitle_10.3', {
							default:
								'⦁	respond to requests or orders from public, government and judition authorities;',
						}),
					],
				},
				{
					title: $format('text_privacy_policy_personal_data_subtitle_11', {
						default: 'Sharing Information with Third Parties:',
					}),
					description: [
						$format('text_privacy_policy_personal_data_subtitle_11.1', {
							default:
								'We may disclose Your Data internally within our business group and to the following entities, but only for the purposes described above. The following categories of Third-Party Service Providers may process your Personal Data as part of our operations:',
						}),
						$format('text_privacy_policy_personal_data_subtitle_11.2', {
							default:
								'⦁	Affiliates - other companies within the group of Shelly Group SE to which Shelly Europe Ltd. belongs to carry out business activities on a regular basis;',
						}),
						$format('text_privacy_policy_personal_data_subtitle_11.3', {
							default:
								'⦁	Integrators of third-party products or services to which you can connect the Service to control those products or services. These business partners control and manage Your personal information only upon your explicit consent to share Your Data with them which you can withdraw anytime. These business partners process Your Data in compliance with their own privacy policies and rules and therefore before deciding to share Your Access to the Service or a Shelly Device with them, You should read these carefully;',
						}),
						$format('text_privacy_policy_personal_data_subtitle_11.4', {
							default:
								'⦁	Service providers - carefully selected companies that provide services for or on our behalf, such as providers of cloud services, customer support services, e-mail and messaging services, including direct marketing services, infrastructure supply and IT services;',
						}),
						$format('text_privacy_policy_personal_data_subtitle_11.5', {
							default:
								'⦁	Payment service providers – licensed payment institutions which facilitate the payment process for pre-paid subscription Services',
						}),
						$format('text_privacy_policy_personal_data_subtitle_11.6', {
							default:
								'⦁	Professionals in various fields (such as but not limited to external marketing, product and service consultants, auditors, legal, finance and accountancy advisors) for maintenance and improvement the quality of the Service, ensuring compliance with regulatory requirements, protection of our legitimate rights and interests in court and administrative proceedings;',
						}),
						$format('text_privacy_policy_personal_data_subtitle_11.7', {
							default:
								'⦁	State bodies and public authorities to which we might be obliged to disclose Your Data when this is required by law, legal process, administrative or court order to disclose your information.',
						}),
						$format('text_privacy_policy_personal_data_subtitle_11.8', {
							default:
								'⦁	Other parties in connection with corporate transactions as part of a merger or transfer, acquisition or sale, or in the event of bankruptcy; In this case, you will receive a clear notification via email and/or our website regarding the change of ownership, the incompatibility of new use of personal information, and the choice of personal information.',
						}),
						$format('text_privacy_policy_personal_data_subtitle_11.9', {
							default:
								'In addition to the disclosures described in this Privacy Notice, we may share information about You with third parties when you separately consent to or request such sharing.',
						}),
						$format('text_privacy_policy_personal_data_subtitle_11.10', {
							default:
								'In regards to private individuals, we require and pay attention that the above stated third parties apply all required technical and organizational measures for the protection of the Personal Data share with them.',
						}),
					],
				},
			],
		},
		{
			id: 'data-transfers',
			title: $format('text_privacy_policy_header_cross_border_data', {
				default: '8. CROSS-BORDER DATA TRANSFERS',
			}),
			titleDesc: [
				$format('text_privacy_policy_cross_border_titleDesc_1', {
					default:
						'Personal Data you entrust to us will primarily be processed by us in European Union. However, some of our Third-Party Service Providers and specifically our providers of e-mail messaging services for direct marketing, are not located in the European Union. The main country outside the European Union where your Personal Data can be processed by such service providers, is the United States. All these international data transfers are subject to legal requirements to ensure that your personal information is processed safely and as you would expect, which means your Personal Data is likely to end up in other countries, including outside the European Union. We will process your Personal Data for marketing purposes including by sharing these with these service providers only upon your explicit consent.',
				}),
			],
		},
		{
			id: 'retention-of-data',
			title: $format('text_privacy_policy_header_retention_data', {
				default: '9. RETENTION OF DATA',
			}),
			bolded: $format('text_privacy_policy_retention_data_bolded', {
				default:
					'The retention period of the Data depends on the legal basis relied upon to process your Data.',
			}),
			titleDesc: [
				$format('text_privacy_policy_retention_data_titleDesc_1', {
					default:
						'Data processed for providing You the Service (Account and Device Data) is processed for as long as You have an active User Account, or as long as required under our legal obligations in the respective countries we operate.',
				}),
				$format('text_privacy_policy_retention_data_titleDesc_2', {
					default:
						'Data processed based on our legitimate interest is only kept for as long as needed for the specific purposes for which they were collected.',
				}),
				$format('text_privacy_policy_retention_data_titleDesc_3', {
					default:
						'Data processed based on your consent are retained until You withdraw your consent. Upon withdrawal of Your consent, we will stop processing your Data that relies on your consent, but it will not impact the processing of data collected prior to Your withdrawal until the purposes for which such Data were collected have been achieved.',
				}),
				$format('text_privacy_policy_retention_data_titleDesc_4', {
					default:
						'Data processed in compliance with legal obligations, are processed within the statutory retention period as per the applicable law, for example the invoice information is kept for 10 years in compliance with the applicable accounting regulatory requirements.',
				}),
				$format('text_privacy_policy_retention_data_titleDesc_content_termination', {
					default:
						'Content upon termination of subscription or deletion of User content or Account: Upon the expiry, cancellation or other form of termination of a paid Premium Service subscription per User’s Shelly House or a Cloud Camera Storage Service subscription, the content stored on Our cloud infrastructure under such subscription (including Cloud Recordings, snapshots, motion-event logs, AI Detection results and associated metadata) is permanently deleted and can no longer be retrieved, restored or downloaded. The same applies where the User deletes individual content or their Account through the Shelly Service; such deletion takes effect upon the User’s request. We do not retain accessible copies of such content and do not provide access to it after termination or deletion.',
				}),
				$format('text_privacy_policy_retention_data_titleDesc_content_download', {
					default:
						'Before the termination of a subscription or the deletion of an Account/ User content takes effect, the User will receive a notification in the Shelly App informing them that the termination or deletion will result in the loss of the service and of the ability to download the content, and that any content the User wishes to keep must be downloaded by the User, in unencrypted form, through the Shelly Service prior to the effective date of termination or deletion. The User is solely responsible for downloading such content in time; We bear no liability for content deleted in accordance with this Article.',
				}),
				$format('text_privacy_policy_retention_data_titleDesc_5', {
					default:
						'Following the expiration of the above-stated time limits, the Data is deleted and may not be retrieved and used any longer.',
				}),
				$format('text_privacy_policy_retention_data_titleDesc_6', {
					default:
						'The data shall not be deleted but shall continue to be processed only for protection of our legitimate rights and interests or in compliance with our legitimate obligations, in the event that as of the date of expiration of the above stated time limit there is pending court, administrative and pre-court proceedings – until its closing.',
				}),
			],
		},
		{
			id: 'rights',
			title: $format('text_privacy_policy_header_rights', {
				default: '10. YOUR RIGHTS AS DATA SUBJECT',
			}),
			bolded: $format('text_privacy_policy_header_data_bolded', {
				default:
					'Under data protection law, you have rights we need to make you aware of. The rights available to you depend on our reason for processing your information.',
			}),
			subtitles: [
				{
					title: $format('text_privacy_policy_header_data_subtitle_1', {
						default: 'Right to access',
					}),
					description: [
						$format('text_privacy_policy_header_data_subtitle_desc_1.1', {
							default:
								'⦁	You have the right to ask us for copies of your personal information free of charge. This right always applies but there are some exemptions, which means you may not always receive all the information we process;',
						}),
						$format('text_privacy_policy_header_data_subtitle_desc_1.2', {
							default:
								'⦁	If we are unable to provide you with access to your Personal Data because disclosure would violate the rights and freedoms of third parties, we will notify you of this decision.',
						}),
						$format('text_privacy_policy_header_data_subtitle_desc_1.3', {
							default:
								'Only a person who can be identified by us as User has the opportunity to exercise his/her rights under this section. You may contact us and after submitting a written request and verifying your identity the requested information will be provided to you.',
						}),
						$format('text_privacy_policy_header_data_subtitle_desc_1.4', {
							default:
								'If We have legitimate concerns regarding Your identity, we may request the provision of additional information necessary to verify Your identity as a User. We reserve the right to refuse access to the required information if we are not in a position to identify the individual submitting a request.',
						}),
					],
				},
				{
					title: $format('text_privacy_policy_header_data_subtitle_2', {
						default: 'Right to rectification of inaccurate personal data',
					}),
					description: [
						$format('text_privacy_policy_header_data_subtitle_desc_2.1', {
							default:
								'You have the right to request the rectification of inaccurate information and completion of incomplete information. This you can accomplish via your Service Account or by contacting us by submitting a written request.',
						}),
					],
				},
				{
					title: $format('text_privacy_policy_header_data_subtitle_3', {
						default: 'Right to erasure („The right to be forgotten“)',
					}),
					description: [
						$format('text_privacy_policy_header_data_subtitle_desc_3.1', {
							default:
								'The right to erasure applies to a strictly limited extent as specified under the law. Removal of the need to process. The right only applies in the following circumstances:',
						}),
						$format('text_privacy_policy_header_data_subtitle_desc_3.2', {
							default:
								'⦁	when your data are no longer necessary for the original reason they were collected or used for.',
						}),
						$format('text_privacy_policy_header_data_subtitle_desc_3.3', {
							default:
								'⦁	when you initially consented to us using your data, but have now withdrawn your consent.',
						}),
						$format('text_privacy_policy_header_data_subtitle_desc_3.4', {
							default:
								'⦁	when you have objected to the use of your data, and your interests outweigh our interests in using it.',
						}),
						$format('text_privacy_policy_header_data_subtitle_desc_3.5', {
							default:
								'⦁	when you have objected to the use of your data for direct marketing purposes.',
						}),
						$format('text_privacy_policy_header_data_subtitle_desc_3.6', {
							default:
								'The right to be forgotten is not an absolute right and might not be respected in cases provided for by the law or because of a lack of reliable verification of Your identity.',
						}),
						$format('text_privacy_policy_header_data_subtitle_desc_3.7', {
							default:
								'You may exercise your right to erasure your data according to the procedure prescribed below or by contacting us, filling out a written request form and verifying your identity. Deleted data cannot be recovered by Shelly Europe Ltd.',
						}),
					],
				},
				{
					title: $format('text_privacy_policy_header_data_subtitle_8', {
						default: 'ACCOUNT DELETION:',
					}),
					description: [
						$format('text_privacy_policy_header_data_subtitle_desc_8.1', {
							default: 'You may request the deletion of Your account:',
						}),
						$format('text_privacy_policy_header_data_subtitle_desc_8.2', {
							default:
								'⦁ via your account in the Аpp or in the Browser through the settings. Deleted account and personal data therein cannot be recovered by Shelly Europe Ltd.',
						}),
						$format('text_privacy_policy_header_data_subtitle_desc_8.3', {
							default: `⦁ by clicking on the following web link ${urlDelete}`,
							values: { url: urlDelete },
						}),
						$format('text_privacy_policy_header_data_subtitle_desc_8.3.1', {
							default:
								'Important: The Cury app (mobile application “Cury” available for Android and iOS) and Shelly Smart Control  operate through a single unified Shelly Platform Account within the shared Shelly Cloud Service. Consequently, deletion of an account initiated via the Cury App or Shelly Smart Control will apply to the Shelly Platform Account as a whole and will remove access to Shelly Smart Control and Cury App, any linked devices, and all related features and functionalities.',
						}),
						$format('text_privacy_policy_header_data_subtitle_desc_8.4', {
							default:
								'Follow our instructions during the process of deletion of your account. Once we have verified your personal identity, we shall proceed further with your request, and shall provide you with the relevant instructions for successful completion of the process for account deletion if the requirements imposed by law are met.',
						}),
						$format('text_privacy_policy_header_data_subtitle_desc_8.5', {
							default:
								'Upon successful completion of the procedure for account deletion, your account data, as well as any other data associated with your account shall be IRREVERSIBLY DELETED.',
						}),
						$format('text_privacy_policy_header_data_subtitle_desc_8.6', {
							default:
								'If you use a Premium Service on one or many of your Shelly House/s, upon successful completion of the procedure for account deletion, YOUR Premium Service applied on the Shelly HOUSE WILL BE TERMINATED and YOU WILL NOT BE ENTITLED TO USE THE Shelly Premium Service applied on the House FOR THE REST OF THE SUBSCRIPTION PERIOD. THE SUBSCRIPTION FEE IS NOT REFUNDABLE. Shelly Europe Ltd. WILL NOT OWE YOU ANY REFUND OF THE SUBSCRIPTION FEE FOR THE REST OF THE SUBSCRIPTION PERIOD, WHICH HAS BEEN PAID FOR BUT NOT USED.',
						}),
						$format('text_privacy_policy_header_data_subtitle_desc_8.7', {
							default:
								'Upon successful completion of the procedure for account deletion THE FREE TRIAL ACCESS TO THE Premium Service applied on the Shelly HOUSE WILL BE TERMINATED AND YOU WILL NOT BE ENTITLED TO USE/TEST THE Premium Service for the Shelly HOUSE FOR THE REST OF THE FREE TRIAL PERIOD.',
						}),
						$format('text_privacy_policy_header_data_subtitle_desc_8.8', {
							default:
								'Despite the completed account deletion if You use a Premium Service on a Shelly House We are obliged by law to keep with us your payment details necessary for the subscription and invoicing us for a period of 10 years in compliance with the applicable accounting regulatory requirements.',
						}),
						$format('text_privacy_policy_header_data_subtitle_desc_8.9', {
							default:
								'UPON SUCCESSFUL COMPLETION OF THE PROCEDURE FOR ACCOUNT DELETION, ANY ACTIVE CLOUD CAMERA STORAGE SERVICE SUBSCRIPTION ASSOCIATED WITH THE ACCOUNT WILL ALSO BE TERMINATED AND YOU WILL NOT BE ENTITLED TO USE THE CLOUD CAMERA STORAGE SERVICE FOR THE REST OF THE THEN-CURRENT BILLING PERIOD. THE MONTHLY SUBSCRIPTION FEE IS NOT REFUNDABLE. Shelly Europe Ltd. WILL NOT OWE YOU ANY REFUND OF THE MONTHLY SUBSCRIPTION FEE FOR THE REST OF THE THEN-CURRENT BILLING PERIOD, WHICH HAS BEEN PAID FOR BUT NOT USED. All Camera Content and related data will be deleted upon successful completion of the Account-deletion process.',
						}),
						$format('text_privacy_policy_header_data_subtitle_desc_8.10', {
							default:
								'House Transfer. Where You initiate a House Transfer of a House to another User’s Account through the Shelly Service, all data and other content associated with that House and with the Shelly Devices assigned to it (including, where applicable, access to the features of active House-level subscriptions) will be transferred together with the House to the receiving User’s Account and the receiving User will become the Main User of that House. From the moment of completion of the House Transfer, You will no longer have access to the transferred House, the Shelly Devices assigned to it, or the related historical data. Any active House-level subscription linked to the transferred House remains billed to You until You cancel it, as set out in the General Terms and Conditions of the Shelly Cloud Service. If the transferred House is the only or last remaining House on Your Account, Your Account will be automatically deleted upon completion of the House Transfer. A House Transfer cannot be reversed unilaterally by Shelly Europe Ltd.',
						}),
					],
				},
				{
					title: $format('text_privacy_policy_header_data_subtitle_9', {
						default: 'MANAGE DATA:',
					}),
					description: [
						$format('text_privacy_policy_header_data_subtitle_desc_9.1', {
							default:
								'In case You do not want to delete Your Account entirely, You may manage Your account data (including the data about a device) through the settings of the Account by choosing to:',
						}),
						$format('text_privacy_policy_header_data_subtitle_desc_9.2', {
							default:
								'⦁ Reset the settings of the Device, reboot the Device, enable night mode, enable eco modes, configure the Device to turn on or off, etc.',
						}),
						$format('text_privacy_policy_header_data_subtitle_desc_9.3', {
							default:
								'⦁ Remove the information for the Device from your account in the App or in the Browser.',
						}),
						$format('text_privacy_policy_header_data_subtitle_desc_9.4', {
							default:
								'⦁ Edit the Account data by changing the e-mail or password associated therewith.',
						}),
					],
				},
				{
					title: $format('text_privacy_policy_header_data_subtitle_4', {
						default: 'Right to restriction of processing',
					}),
					description: [
						$format('text_privacy_policy_header_data_subtitle_desc_4.1', {
							default:
								'Right to restriction of processing shall apply to temporarily limit the use of your data when they are considering:',
						}),
						$format('text_privacy_policy_header_data_subtitle_desc_4.2', {
							default: '⦁	the accuracy of Your data is challenged, or',
						}),
						$format('text_privacy_policy_header_data_subtitle_desc_4.3', {
							default: '⦁	an objection to the use of Your Data.',
						}),
						$format('text_privacy_policy_header_data_subtitle_desc_4.4', {
							default:
								'⦁	unlawful processing of Data but you do not want it to be deleted, or',
						}),
						$format('text_privacy_policy_header_data_subtitle_desc_4.5', {
							default:
								'⦁  Data are no longer needs your data but you want the organisation to keep it to create, exercise or defend legal claims.',
						}),
						$format('text_privacy_policy_header_data_subtitle_desc_4.6', {
							default:
								'In case of rectification or erasure of processing, we will notify every recipient to whom Your Personal Data has been disclosed unless this is impossible or requires disproportionately huge efforts.',
						}),
					],
				},
				{
					title: $format('text_privacy_policy_header_data_subtitle_5', {
						default: 'Right of Data portability',
					}),
					description: [
						$format('text_privacy_policy_header_data_subtitle_desc_5.1', {
							default:
								'You have the right to get your Personal Data from us in a way that is accessible and machine-readable. You also have the right to ask for transferring Your data to another organisation. Specifically, the right only applies to data that:',
						}),
						$format('text_privacy_policy_header_data_subtitle_desc_5.2', {
							default: '⦁	is held electronically, and',
						}),
						$format('text_privacy_policy_header_data_subtitle_desc_5.3', {
							default: '⦁	you have provided to us.',
						}),
						$format('text_privacy_policy_header_data_subtitle_desc_5.4', {
							default:
								'Data you have provided does not just mean information you have typed in, such as a username or email address. It may include data that We have gathered from monitoring Your activities when you have used the Service. This may include usage history or data processed by connected objects such as the Device.',
						}),
						$format('text_privacy_policy_header_data_subtitle_desc_5.5', {
							default: 'That right shall not apply:',
						}),
						$format('text_privacy_policy_header_data_subtitle_desc_5.6', {
							default:
								'⦁	to processing necessary for the performance of a task carried out in the public interest;',
						}),
						$format('text_privacy_policy_header_data_subtitle_desc_5.7', {
							default:
								'⦁	where this right would affect adversely the rights and freedoms of others.',
						}),
					],
				},
				{
					title: $format('text_privacy_policy_header_data_subtitle_7', {
						default:
							'Right to object to the processing when it’s based on legitimate interest:',
					}),
					description: [
						$format('text_privacy_policy_header_data_subtitle_desc_7.1', {
							default: 'You can only object to processing when We are using your data:',
						}),
						$format('text_privacy_policy_header_data_subtitle_desc_7.2', {
							default: '⦁	for legitimate interests;',
						}),
						$format('text_privacy_policy_header_data_subtitle_desc_7.3', {
							default: '⦁	for statistical purposes; or',
						}),
						$format('text_privacy_policy_header_data_subtitle_desc_7.4', {
							default: '⦁	for direct marketing purposes.',
						}),
						$format('text_privacy_policy_header_data_subtitle_desc_7.5', {
							default:
								'The processing of Your data will be ceased, unless there are compelling legitimate grounds for the processing which override Your interests, rights and freedoms or for the establishment, exercise ordefence of legal claims.',
						}),
					],
				},
				{
					title: $format('text_privacy_policy_header_data_subtitle_6', {
						default:
							'Right to file a complaint with the supervisory authority in the Member State of your residence, place of work or place of the alleged infringement if you consider that the processing of personal data relating to You infringes the GDPR.',
					}),
					description: [
						$format('text_privacy_policy_header_data_subtitle_desc_6.1', {
							default:
								'Users have the right to submit complaints or signals to the supervisory authority at any time, in case they believe the processing of their personal data violates the legislation on personal data protection.',
						}),
						$format('text_privacy_policy_header_data_subtitle_desc_6.2', {
							default:
								'Without prejudice to your right of complaint to the supervisory authority at any time, please contact us in advance and we promise to make everything possible to settle any disputes amicably.',
						}),
					],
				},
			],
		},
		{
			id: 'security-measures',
			title: $format('text_privacy_policy_header_security_measures', {
				default: '11. INFORMATION SECURITY MEASURES',
			}),
			titleDesc: [
				$format('text_privacy_policy_security_measures_titleDesc_1', {
					default:
						'The security, integrity, and confidentiality of your Personal Data are extremely important to us. We have implemented technical, contractual, organisational, and physical security measures that are designed to protect our Users Personal Data from unauthorised access, disclosure, use, and modification. We regularly review our security procedures and practices to consider appropriate new technology and methods. Please be aware that, despite our best efforts, no security measures are perfect or impenetrable.',
				}),
			],
		},
		{
			id: 'updates',
			title: $format('text_privacy_policy_header_updates', {
				default: '12. PRIVACY POLICY UPDATES',
			}),
			titleDesc: [
				$format('text_privacy_policy_updates_titleDesc_1', {
					default:
						'We may update this Privacy Policy from time to time due to changes in the Service, the applicable laws and our legitimate interest. You can determine when the Privacy Policy was last revised by the date provided at the beginning of this page.',
				}),
				$format('text_privacy_policy_updates_titleDesc_2', {
					default:
						'Any changes will become effective upon publishing in the application or making them available to the User in other way.',
				}),
			],
		},
		{
			id: 'further-information',
			title: $format('text_privacy_policy_header_further_info', {
				default: '13. FURTHER INFORMATION',
			}),
			titleDesc: [
				$format('text_privacy_policy_further_info_titleDesc_1', {
					default:
						'If you have any additional questions, please do not hesitate to contact us at: dpo@shelly.com',
				}),
			],
		},
	];

	$: latestVersion = localizedLastUpdate(
		CHANGE_DATE.PP * 1000,
		$locale,
		'privacy_policy_last_update',
		$format,
	);

	$: title = $format('privacy', { default: 'Privacy policy' });
</script>

{#if $showNewLayout}
	<DocumentContextualNavigationV2 {title} {menuItems} {sectionsContent} {latestVersion} />
{:else}
	<DocumentContextualNavigation {title} {menuItems} {sectionsContent} {latestVersion} />
{/if}
